import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config';
import { getAuth } from 'firebase/auth';

// Image categories and their storage paths
export const IMAGE_CATEGORIES = {
  MARINA: 'marina-images',
  VESSELS: 'vessel-images',
  SLIPS: 'slip-images',
  GALLERY: 'gallery-images',
  BANNERS: 'banner-images',
  LOGOS: 'logo-images',
  ID: 'id-images', // Secure area for ID/license photos
} as const;

export type ImageCategory = keyof typeof IMAGE_CATEGORIES;

export interface ImageMetadata {
  id: string;
  storageUrl: string;
  category: ImageCategory;
  filename: string;
  title?: string;
  altText?: string;
  description?: string;
  tags?: string[];
  isPublic: boolean;
  uploadedBy?: string;
  uploadedAt?: number;
}

/**
 * Upload an image to Firebase Storage and save its metadata in Firestore
 */
export const uploadImage = async (
  file: File,
  category: ImageCategory,
  metadata: Partial<ImageMetadata> = {}
): Promise<ImageMetadata> => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('You must be logged in to upload images');
  }
  
  try {
    // Generate a unique filename
    const filename = `${Date.now()}-${file.name}`;
    const storageRef = ref(storage, `${IMAGE_CATEGORIES[category]}/${filename}`);
    
    // Upload to Storage
    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);
    
    // Save metadata to Firestore
    const imageData: Omit<ImageMetadata, 'id'> = {
      storageUrl: downloadUrl,
      category,
      filename,
      isPublic: true,
      title: metadata.title || '',
      altText: metadata.altText || '',
      tags: metadata.tags || [],
      uploadedBy: user.uid,
      uploadedAt: Date.now()
    };
    
    const docRef = await addDoc(collection(db, 'images'), imageData);
    
    // Return the complete image metadata
    return {
      ...imageData,
      id: docRef.id
    };
  } catch (error: any) {
    console.error('Error uploading image:', error);
    throw new Error(error.message || 'Failed to upload image');
  }
};

/**
 * Get all images by category
 */
export const getImages = async (
  category: ImageCategory,
  publicOnly: boolean = false
): Promise<ImageMetadata[]> => {
  try {
    let q = query(collection(db, 'images'), where('category', '==', category));
    
    if (publicOnly) {
      q = query(q, where('isPublic', '==', true));
    }
    
    const snapshot = await getDocs(q);
    const images: ImageMetadata[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data() as Omit<ImageMetadata, 'id'>;
      images.push({
        ...data,
        id: doc.id
      } as ImageMetadata);
    });
    
    return images;
  } catch (error: any) {
    console.error('Error getting images:', error);
    throw new Error(error.message || 'Failed to get images');
  }
};

/**
 * Update image metadata
 */
export const updateImageMetadata = async (
  imageId: string,
  updates: Partial<Omit<ImageMetadata, 'id' | 'storageUrl' | 'category' | 'filename'>>
): Promise<void> => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('You must be logged in to update image metadata');
  }
  
  try {
    const docRef = doc(db, 'images', imageId);
    await updateDoc(docRef, updates);
  } catch (error: any) {
    console.error('Error updating image metadata:', error);
    throw new Error(error.message || 'Failed to update image metadata');
  }
};

/**
 * Delete an image and its metadata
 */
export const deleteImage = async (imageId: string): Promise<void> => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('You must be logged in to delete images');
  }

  try {
    // Get the image metadata first
    const imageSnapshot = await getDocs(query(collection(db, 'images'), where('id', '==', imageId)));
    if (imageSnapshot.empty) {
      throw new Error('Image not found');
    }
    
    const imageData = imageSnapshot.docs[0].data() as ImageMetadata;
    
    // Delete from Storage
    const storageRef = ref(storage, `${IMAGE_CATEGORIES[imageData.category]}/${imageData.filename}`);
    await deleteObject(storageRef);

    // Delete from Firestore
    const docRef = doc(db, 'images', imageId);
    await deleteDoc(docRef);
  } catch (error: any) {
    console.error('Error deleting image:', error);
    throw new Error(error.message || 'Failed to delete image');
  }
}; 